import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type { DomainOrder } from '@breaker/shared-types';

/**
 * Domain purchase rail (U3/U6) — the "Get a domain" door's client half.
 * Approved copy: docs/domain-purchase-drafts/fleet-settings-three-door-copy.md.
 */

export interface DomainOrdersConfig {
  enabled: boolean;
  tlds: Array<{ tld: string; retailPence: number }>;
  /** 0085 — the founding comp-code entitlement. `available: false` for every
   *  fleet that never redeemed a code, which is every fleet today, so the
   *  door renders exactly as it always has. */
  compEntitlement: { available: boolean };
}

export interface DomainCandidate {
  domain: string;
  available: boolean;
  premium: boolean;
  retailPence: number | null;
}

/** How the server read the query — exact typed domain leads the candidates;
 *  an unsold typed TLD is stripped and surfaced here for honest copy. */
export interface DomainSearchParsed {
  label: string;
  typedTld: string | null;
  unsupportedTld: string | null;
}

export interface DomainSearchResult {
  candidates: DomainCandidate[];
  parsed: DomainSearchParsed;
}

export interface DomainRegistrantInput {
  firstName: string;
  lastName: string;
  organization?: string;
  email: string;
  address1: string;
  city: string;
  postalCode: string;
  phone: string;
}

const configKey = (fleetId: string | null) =>
  ['fleets', fleetId ?? '', 'domain-orders', 'config'] as const;
const ordersKey = (fleetId: string | null) =>
  ['fleets', fleetId ?? '', 'domain-orders'] as const;

export function useDomainOrdersConfig(fleetId: string | null) {
  return useQuery({
    queryKey: configKey(fleetId),
    enabled: Boolean(fleetId),
    queryFn: () => api<DomainOrdersConfig>(`/v1/fleets/${fleetId}/domain-orders/config`),
    staleTime: 5 * 60_000,
  });
}

export function useDomainOrders(fleetId: string | null) {
  return useQuery({
    queryKey: ordersKey(fleetId),
    enabled: Boolean(fleetId),
    queryFn: () => api<{ orders: DomainOrder[] }>(`/v1/fleets/${fleetId}/domain-orders`),
    select: (d) => d.orders,
    // A paid order registers in the background — keep the panel fresh while
    // someone watches it.
    refetchInterval: (q) =>
      (q.state.data?.orders ?? []).some(
        (o) => o.status === 'fulfilling' || o.status === 'pending_operation',
      )
        ? 10_000
        : false,
  });
}

export function useDomainSearch(fleetId: string | null) {
  return useMutation({
    mutationFn: (query: string) =>
      api<DomainSearchResult>(
        `/v1/fleets/${fleetId}/domain-orders/search`,
        { method: 'POST', body: { query } },
      ),
  });
}

export function useCreateDomainOrder(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      domain: string;
      registrant: DomainRegistrantInput;
      /** Required: use these details to register in the buyer's name. */
      registrationConsent: boolean;
      /** Optional WHOIS-privacy preference (default on server-side). */
      whoisPrivacy?: boolean;
      /** 'wizard' returns Stripe to /signup; default 'settings' door. */
      returnTo?: 'wizard' | 'settings';
    }) =>
      // 0085: checkoutUrl is NULL for a comp order — there is no card step,
      // and the caller goes straight to the polling panel instead of
      // redirecting to Stripe.
      api<{ order: DomainOrder; checkoutUrl: string | null }>(
        `/v1/fleets/${fleetId}/domain-orders`,
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ordersKey(fleetId) }),
  });
}

export function useCancelDomainOrder(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      api<{ cancelled: boolean }>(
        `/v1/fleets/${fleetId}/domain-orders/${orderId}/cancel`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ordersKey(fleetId) }),
  });
}
